import { styled } from "~/ui/style/stitches.config";
import { imageUrl } from "~/utils/imageurl";

// Animation configuration
const STACK_SIZE = 8;
const FAN_CARD_COUNT = 3; // Top 3 cards fan out
const STACK_ROTATIONS = [-1.2, 1.5, -0.8, 2, -1.5, 0.9, -2, 0]; // Messy hand-shuffled look

interface GuidebookCardStackProps {
  images: (string | null | undefined)[];
  fallbackImage: string;
  isHovered: boolean;
  isLoading: boolean;
}

export const GuidebookCardStack = ({
  images,
  fallbackImage,
  isHovered,
  isLoading,
}: GuidebookCardStackProps) => {
  // Stabilize images array - only update when we have enough cards loaded
  // This prevents the flash of duplicate cards during progressive loading
  const hasEnoughImages = images.length >= FAN_CARD_COUNT;

  return (
    <CardStackContainer className={`${isHovered && hasEnoughImages ? "hovered" : ""} ${isLoading ? "loading" : ""}`}>
      {[...Array(STACK_SIZE)].map((_, i) => {
        const isTopCard = i === STACK_SIZE - 1;
        const isAnimatedCard = i >= STACK_SIZE - FAN_CARD_COUNT;

        // Card image: index 7 shows 1st card (The Fool/Ace), etc.
        const cardImageIndex = STACK_SIZE - 1 - i;
        const cardImage = images[cardImageIndex] || fallbackImage;

        // Stack transform values
        const stackRotateZ = STACK_ROTATIONS[i] + (isTopCard ? 0 : 0.5);
        const stackTranslateZ = (i - (STACK_SIZE - 1)) * 2;
        const stackOffset = isTopCard ? "translate(0, 0)" : "translate(-2px, 9px)";

        // Fan transform values (for top 3 cards)
        const fanIndex = i - (STACK_SIZE - FAN_CARD_COUNT); // 0, 1, 2
        const fanRotation = isAnimatedCard ? (1 - fanIndex) * 15 : 0;
        const fanTranslateX = isAnimatedCard ? (1 - fanIndex) * 40 : 0;

        // Compress position for bottom cards
        const compressTranslateZ = (i - 4) * 1;

        return (
          <StackCardImage
            key={`${i}-${cardImage}`}
            className={`${isHovered && hasEnoughImages && isAnimatedCard ? "fan-card" : ""} ${isHovered && hasEnoughImages && !isAnimatedCard ? "compress-card" : ""}`}
            src={imageUrl(cardImage, 200)}
            alt=""
            style={{
              position: isTopCard ? "relative" : "absolute",
              zIndex: i,
              boxShadow: isTopCard ? "-2px 2px 4px rgba(0, 0, 0, 0.10)" : undefined,
              "--stack-rotate-z": `${stackRotateZ}deg`,
              "--stack-translate-z": `${stackTranslateZ}px`,
              "--stack-offset": stackOffset,
              "--fan-rotation": `${fanRotation}deg`,
              "--fan-translate-x": `${fanTranslateX}px`,
              "--compress-translate-z": `${compressTranslateZ}px`,
            } as React.CSSProperties}
          />
        );
      })}
    </CardStackContainer>
  );
};

const CardStackContainer = styled("div", {
  perspective: "1200px",
  perspectiveOrigin: "right bottom",
  transformStyle: "preserve-3d",
  position: "relative",
  transition: "transform 0.4s ease, opacity 0.3s ease",

  // Hide cards until images are loaded to prevent flash
  "&.loading": {
    opacity: 0,
  },

  // Ground shadow - use translateZ instead of zIndex for proper 3D stacking
  "&::after": {
    content: '""',
    position: "absolute",
    width: "80%",
    height: "15px",
    bottom: "-45px",
    left: "10%",
    backgroundColor: "rgba(0, 0, 0, 0.15)",
    transform: "skewX(50deg) rotate(-6deg) translateZ(-1px)",
    filter: "blur(10px)",
    transition: "all 0.4s ease",
  },
  "@mobile": {
    // Disable preserve-3d on mobile to fix shadow disappearing after animation
    transformStyle: "flat",
    "&::after": {
      width: "80%",
      filter: "blur(7px)",
      bottom: "-40px",
      height: "10px",
      zIndex: -1,
      transform: "skewX(50deg) rotate(-6deg)",
      willChange: "transform, width, left",
    },
  },

  // Drop down on hover, flatten perspective, and widen shadow for fan
  "&.hovered": {
    transform: "translateY(15px) rotate(-3deg)",
    perspective: "none",
    "&::after": {
      filter: "blur(8px)",
      width: "150%",
      left: "-25%",
      transform: "skewX(0deg) rotate(0deg)",
    },
    "@mobile": {
      // On mobile, ensure shadow stays visible with proper stacking
      transformStyle: "flat",
      "&::after": {
        zIndex: -1,
        // Ensure transform doesn't use translateZ on mobile
        transform: "skewX(0deg) rotate(0deg)",
      },
    },
  },
});

const StackCardImage = styled("img", {
  width: "100px",
  height: "auto",
  borderRadius: "8px",
  border: "1px solid rgba(159, 159, 159, 0.8)",
  position: "relative",
  transformStyle: "preserve-3d",
  transition: "transform 0.4s ease, opacity 0.3s ease 0.3s",
  transformOrigin: "center bottom",
  // Default: 3D messy stack
  transform: "rotateX(-30deg) rotateY(20deg) translateZ(var(--stack-translate-z)) rotateZ(var(--stack-rotate-z)) var(--stack-offset)",

  // Fan out on hover - flatten and spread
  "&.fan-card": {
    transform: "rotateX(0deg) rotateY(0deg) translateZ(0) rotate(var(--fan-rotation)) translateX(var(--fan-translate-x))",
  },

  // Bottom cards hide instantly when fanning, delay reappearance
  "&.compress-card": {
    opacity: 0,
    transition: "transform 0.3s ease, opacity 0s ease",
  },

  "@mobile": {
    width: "80px",
  },
});

